import React, { useEffect, useMemo, useRef, useState } from "react";
import "./my-profile.scss";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle, BadgeCheck, CalendarDays, Camera, Gift, KeyRound,
  LockKeyhole, LogOut, MailCheck, MapPin, Pencil, PhoneCall, Plane,
  Plus, ShieldCheck, Star, Trash2, UserRound, Users, WalletCards,
} from "lucide-react";
import { Button, Card, Divider, Form, Input, Modal, Select, Skeleton, Space, Typography } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import MyBookings from "../MyBookings/MyBookings";
import { clearUser, setUser } from "../../Slices/User/userDataSlice";
import { sendEmailOTP, updateUserProfile } from "../../requests/authApi";
import { updateProfile } from "../../requests/usersAPI";
import { get, post } from "../../utils/request";
import { setCurrency } from "../../utils/context";
import { phoneCountryCodes, iso2ToDialCode } from "../../constants/phoneCountryCodes";
import {
  emptyFrequentFlyerMembership,
  frequentFlyerAirlineByCode,
  frequentFlyerAirlines,
  normalizeFrequentFlyerMembership,
} from "./frequentFlyerAirlines";

const { Title, Text } = Typography;
const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", postalCode: "" };
const EMPTY_TRAVELLER = {
  firstName: "", lastName: "", dob: "", gender: "", nationality: "",
  issuingCountry: "", documentType: "Passport", documentNumber: "",
  expirationDate: "", noExpirationDate: false, frequentFlyerMemberships: [],
};
const currencyOptions = ["USD", "SGD", "MYR", "CNY", "HKD"].map((value) => ({ value, label: value }));
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const extractISO2 = (country) =>
  (country?.iso2 || country?.iso2_cc || country?.alpha2 || country?.code2 || country?.cca2 || "")
    .toString().toUpperCase();

const extractDialCode = (country) => {
  const direct = country?.dial_code || country?.dialCode || country?.phone_code || country?.phoneCode || country?.code;
  if (typeof direct === "string" && /^\+\d{1,4}$/.test(direct)) return direct;
  return iso2ToDialCode?.[extractISO2(country)] || null;
};

const FlagOptionLabel = ({ name, dialCode, flagUrl }) => (
  <span className="mp-select-option">
    {flagUrl ? (
      <img className="mp-country-flag" src={flagUrl} alt="" loading="lazy" />
    ) : (
      <span className="mp-country-flag-fallback" aria-hidden="true">🌐</span>
    )}
    <span>{dialCode || name}</span>
  </span>
);

const parseAddress = (address = "", country = "") => {
  const raw = String(address).split(/\n|,/).map((part) => part.trim());
  if (raw.length >= 6) {
    return { line1: raw[0], line2: raw[1], city: raw[2], state: raw[3], postalCode: raw[4] };
  }
  const parts = raw.filter(Boolean);
  if (parts.at(-1)?.toLowerCase() === String(country).trim().toLowerCase()) parts.pop();
  if (parts.length >= 5) return { line1: parts[0], line2: parts[1], city: parts[2], state: parts[3], postalCode: parts[4] };
  if (parts.length === 4) return { line1: parts[0], line2: parts[1], city: parts[2], state: "", postalCode: parts[3] };
  if (parts.length === 3) return { line1: parts[0], line2: "", city: parts[1], state: "", postalCode: parts[2] };
  return { ...EMPTY_ADDRESS, line1: parts[0] || "" };
};

const composeAddress = (address, country) =>
  [address.line1, address.line2, address.city, address.state, address.postalCode, country]
    .map((part) => String(part || "").trim()).join(", ");

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat("en-SG", { dateStyle: "medium" }).format(date);
};

const formatCreditBalance = (value) => Number(value || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toDayFirstDob = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const appFormat = raw.match(/^(\d{1,2})[/-]([A-Za-z]{3})[/-](\d{4})$/);
  if (appFormat) return `${appFormat[1].padStart(2, "0")}/${appFormat[2][0].toUpperCase()}${appFormat[2].slice(1).toLowerCase()}/${appFormat[3]}`;
  const numericFormat = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (numericFormat) return `${numericFormat[1].padStart(2, "0")}/${MONTHS[Number(numericFormat[2]) - 1] || numericFormat[2]}/${numericFormat[3]}`;
  const isoFormat = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoFormat) return `${isoFormat[3]}/${MONTHS[Number(isoFormat[2]) - 1]}/${isoFormat[1]}`;
  return raw;
};

const normalizeDayFirstDob = (value) => {
  const normalized = toDayFirstDob(value);
  const match = normalized.match(/^(\d{2})[/-]([A-Za-z]{3})[/-](\d{4})$/);
  if (!match) return null;
  const monthIndex = MONTHS.findIndex((month) => month.toLowerCase() === match[2].toLowerCase());
  const day = Number(match[1]);
  const year = Number(match[3]);
  if (monthIndex < 0 || year < 1900) return null;
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== monthIndex || date.getUTCDate() !== day || date > new Date()) return null;
  return `${String(day).padStart(2, "0")}/${MONTHS[monthIndex]}/${year}`;
};

const toNumericDayFirst = (value) => {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
};

const dayFirstToIso = (value) => {
  const match = String(value || "").trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (year < 1900 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day || date > new Date()) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const SectionHeader = ({ icon: Icon, title, description, action }) => (
  <div className="mp-section-header">
    <div className="mp-section-title">
      <span className="mp-icon"><Icon size={21} /></span>
      <span><Title level={5}>{title}</Title>{description && <Text>{description}</Text>}</span>
    </div>
    {action && <div className="mp-section-action">{action}</div>}
  </div>
);

const StatusPill = ({ verified, children }) => (
  <span className={`mp-status ${verified ? "is-verified" : "is-pending"}`}>
    {verified ? <BadgeCheck size={15} /> : <AlertTriangle size={15} />}{children}
  </span>
);

const MyProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const userData = useSelector((state) => state.userData.user);
  const accountUser = userData?.user;

  const [activeTab, setActiveTab] = useState("profile");
  const [isSessionResolving, setIsSessionResolving] = useState(true);
  const [profileEditable, setProfileEditable] = useState(false);
  const [profileFormData, setProfileFormData] = useState(null);
  const [addressFields, setAddressFields] = useState(EMPTY_ADDRESS);
  const [countryCode, setCountryCode] = useState("+65");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [membership, setMembership] = useState(null);
  const [activity, setActivity] = useState({ xpLogs: [], creditLogs: [] });
  const [referrals, setReferrals] = useState([]);
  const [moments, setMoments] = useState({ followerCount: 0, followingCount: 0, postCount: 0 });
  const [travellers, setTravellers] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [travellerModal, setTravellerModal] = useState(false);
  const [travellerForm, setTravellerForm] = useState(EMPTY_TRAVELLER);
  const [savingTraveller, setSavingTraveller] = useState(false);
  const [deleteTraveller, setDeleteTraveller] = useState(null);
  const [openChangePwd, setOpenChangePwd] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verificationModal, setVerificationModal] = useState(null);
  const [verificationOtp, setVerificationOtp] = useState("");
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [verificationRetryAfter, setVerificationRetryAfter] = useState(0);
  const [verifiedContacts, setVerifiedContacts] = useState({ email: false, phone: false });
  const [contactVerificationIds, setContactVerificationIds] = useState({ email: null, phone: null });
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [deactivating, setDeactivating] = useState(false);
  const [countryFlags, setCountryFlags] = useState({});

  useEffect(() => {
    if (verificationRetryAfter <= 0) return undefined;
    const timer = window.setInterval(() => {
      setVerificationRetryAfter((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [verificationRetryAfter]);

  const countryOptions = useMemo(() => (phoneCountryCodes || []).map((country) => ({
    value: country.name,
    label: <FlagOptionLabel
      name={country.name}
      flagUrl={countryFlags?.[extractISO2(country)]?.square_image_url}
    />,
    searchText: `${extractISO2(country)} ${country.name}`,
  })), [countryFlags]);

  const phoneCountryOptions = useMemo(() => (phoneCountryCodes || [])
    .map((country) => {
      const iso2 = extractISO2(country);
      const dial = extractDialCode(country);
      return dial ? {
        value: dial,
        label: <FlagOptionLabel dialCode={dial} flagUrl={countryFlags?.[iso2]?.square_image_url} />,
        searchText: `${iso2} ${country.name} ${dial}`,
      } : null;
    })
    .filter(Boolean)
    .filter((option, index, list) => list.findIndex((item) => item.value === option.value) === index), [countryFlags]);

  const filterCountryOption = (input, option) =>
    String(option?.searchText || "").toLowerCase().includes(String(input || "").toLowerCase());

  const logout = () => {
    dispatch(clearUser());
    localStorage.removeItem("teleport_token");
    setCurrency("USD");
    navigate("/");
  };

  useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      if (accountUser?._id) { setIsSessionResolving(false); return; }
      const token = localStorage.getItem("teleport_token");
      if (!token) {
        if (userData) dispatch(clearUser());
        setIsSessionResolving(false);
        navigate("/login", { replace: true, state: { from: location.pathname } });
        return;
      }
      try {
        const session = await get("/users/currentUser", { enrich: true });
        if (cancelled) return;
        if (!session?.user?._id) throw new Error("Unable to restore the signed-in account");
        dispatch(setUser(session));
        if (session.token) localStorage.setItem("teleport_token", session.token);
        setCurrency(session.user.currency || "USD");
      } catch (error) {
        if (cancelled) return;
        dispatch(clearUser());
        localStorage.removeItem("teleport_token");
        setCurrency("USD");
        navigate("/login", { replace: true, state: { from: location.pathname } });
      } finally {
        if (!cancelled) setIsSessionResolving(false);
      }
    };
    restoreSession();
    return () => { cancelled = true; };
  }, [accountUser?._id, dispatch, location.pathname, navigate, userData]);

  useEffect(() => {
    if (!accountUser?._id) return;
    const initial = {
      userId: accountUser._id, fullName: accountUser.fullName || "", email: accountUser.email || "",
      DOB: toDayFirstDob(accountUser.DOB), gender: accountUser.gender || "", currency: accountUser.currency || "USD",
      country: accountUser.country || "",
      address: accountUser.address || "", phoneAreaCode: accountUser.phoneAreaCode || "+65", phone: accountUser.phone || "",
    };
    setProfileFormData(initial);
    setAddressFields(parseAddress(initial.address, initial.country));
    setCountryCode(initial.phoneAreaCode || "+65");
    setPhone(initial.phone);
    setVerifiedContacts({ email: accountUser.isVerified === true, phone: Boolean(accountUser.phone) });
    if (["profile", "bookings", "travellers", "verification"].includes(location.state?.activeTab)) {
      setActiveTab(location.state.activeTab);
    }
  }, [accountUser, location.state]);

  useEffect(() => {
    let cancelled = false;
    const loadCountryFlags = async () => {
      try {
        const flags = await get("/appConfig/countryFlags");
        if (!cancelled && flags && typeof flags === "object") setCountryFlags(flags);
      } catch (error) {
        if (!cancelled) setCountryFlags({});
      }
    };
    loadCountryFlags();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!accountUser?._id) return;
    let cancelled = false;
    const loadDashboard = async () => {
      setLoadingDashboard(true);
      const results = await Promise.allSettled([
        get("/membership/summary"), get("/membership/activity"),
        get("/users/referralLogs", { userId: accountUser._id }),
        get("/moments/profile/me"), get("/passengers/getAll"),
      ]);
      if (cancelled) return;
      if (results[0].status === "fulfilled") setMembership(results[0].value);
      if (results[1].status === "fulfilled") setActivity(results[1].value || { xpLogs: [], creditLogs: [] });
      if (results[2].status === "fulfilled") setReferrals(Array.isArray(results[2].value?.referralLogs) ? results[2].value.referralLogs : []);
      if (results[3].status === "fulfilled") setMoments(results[3].value || {});
      if (results[4].status === "fulfilled") setTravellers(Array.isArray(results[4].value) ? results[4].value : []);
      setLoadingDashboard(false);
    };
    loadDashboard();
    return () => { cancelled = true; };
  }, [accountUser?._id]);

  const formik = useFormik({
    initialValues: { currentPassword: "", newPassword: "", confirmNewPassword: "", otp: "" },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current Password is required"),
      newPassword: Yup.string().min(6, "New Password must be at least 6 characters").required("New Password is required"),
      confirmNewPassword: Yup.string().oneOf([Yup.ref("newPassword"), null], "Passwords do not match").required("Confirm New Password is required"),
      otp: Yup.string().required("OTP is required"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        await updateUserProfile({ email: accountUser.email, oldPassword: values.currentPassword, newPassword: values.newPassword, otp: values.otp, userId: accountUser._id });
        toast.success("Password changed");
        setOpenChangePwd(false); setOtpSent(false); helpers.resetForm();
      } catch (error) { toast.error(error?.message || "Change password failed"); }
      finally { helpers.setSubmitting(false); }
    },
  });

  const updateSessionUser = (session) => {
    dispatch(setUser(session));
    setCurrency(session?.user?.currency || "USD");
  };

  const saveProfileInformation = async () => {
    if (!phone.trim()) { setPhoneError("Phone number is required"); return; }
    if (!addressFields.line1.trim() || !addressFields.city.trim() || !addressFields.postalCode.trim()) {
      toast.error("Address line 1, city, and postal code are required"); return;
    }
    const originalPhone = String(accountUser.phone || "").trim();
    const originalAreaCode = String(accountUser.phoneAreaCode || "+65").trim();
    const originalEmail = String(accountUser.email || "").trim().toLowerCase();
    const nextEmail = String(profileFormData.email || "").trim().toLowerCase();
    const emailChanged = nextEmail !== originalEmail;
    const phoneChanged = phone.trim() !== originalPhone || countryCode !== originalAreaCode;
    if (!nextEmail) {
      toast.error("Email is required");
      return;
    }
    if (emailChanged && (!verifiedContacts.email || !contactVerificationIds.email)) {
      toast.error("Verify your new email address before saving");
      return;
    }
    if (phoneChanged && (!verifiedContacts.phone || !contactVerificationIds.phone)) {
      toast.error("Verify your new phone number before saving");
      return;
    }
    const normalizedDob = accountUser.DOB ? undefined : normalizeDayFirstDob(profileFormData.DOB);
    if (!accountUser.DOB && profileFormData.DOB && !normalizedDob) {
      toast.error("Enter date of birth as DD/MMM/YYYY, for example 17/Nov/1993");
      return;
    }
    setPhoneError("");
    try {
      const profileResult = await post("/users/updateProfileV2", {
        fullName: profileFormData.fullName,
        email: nextEmail,
        emailVerificationId: emailChanged ? contactVerificationIds.email : null,
        ...(normalizedDob ? { DOB: normalizedDob } : {}),
        gender: profileFormData.gender,
        country: profileFormData.country,
        address: composeAddress(addressFields, profileFormData.country),
        phone: phone.trim(),
        phoneAreaCode: countryCode,
        phoneVerificationId: phoneChanged ? contactVerificationIds.phone : null,
      });
      let result = { ...userData, user: profileResult.user };
      if (profileFormData.currency !== accountUser.currency) {
        result = await updateProfile({ userId: accountUser._id, currency: profileFormData.currency });
      }
      updateSessionUser(result);
      setContactVerificationIds({ email: null, phone: null });
      setProfileEditable(false);
      toast.success("Profile and address updated across Teleport Now");
    } catch (error) { toast.error(error?.message || "Unable to save profile"); }
  };

  const uploadProfilePhoto = async (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Profile photo must be 5 MB or smaller");
    const formData = new FormData(); formData.append("file", file); setUploadingPhoto(true);
    try {
      const result = await post("/users/profileImage", formData, { "Content-Type": "multipart/form-data" });
      dispatch(setUser({ ...userData, user: result.user })); toast.success("Profile photo updated");
    } catch (error) { toast.error(error?.message || "Unable to upload profile photo"); }
    finally { setUploadingPhoto(false); }
  };

  const openTravellerEditor = (traveller = null) => {
    const memberships = (traveller?.frequentFlyerMemberships || []).map(normalizeFrequentFlyerMembership);
    setTravellerForm(traveller ? {
      ...EMPTY_TRAVELLER,
      ...traveller,
      dob: toNumericDayFirst(traveller.dob),
      frequentFlyerMemberships: memberships.length ? memberships : [emptyFrequentFlyerMembership()],
    } : { ...EMPTY_TRAVELLER, frequentFlyerMemberships: [emptyFrequentFlyerMembership()] });
    setTravellerModal(true);
  };

  const saveTraveller = async () => {
    if (!travellerForm.firstName.trim() || !travellerForm.lastName.trim() || !travellerForm.dob) {
      toast.error("First name, last name, and date of birth are required"); return;
    }
    const travellerDob = dayFirstToIso(travellerForm.dob);
    if (!travellerDob) {
      toast.error("Enter traveller date of birth as DD/MM/YYYY");
      return;
    }
    const frequentFlyerMemberships = [];
    for (const rawMembership of travellerForm.frequentFlyerMemberships) {
      const membership = normalizeFrequentFlyerMembership(rawMembership);
      const hasAirline = Boolean(membership.airlineCode || membership.airlineName);
      const hasNumber = Boolean(membership.membershipNumber.trim());
      if (!hasAirline && !hasNumber) continue;
      const airline = frequentFlyerAirlineByCode.get(membership.airlineCode);
      if (!airline || !hasNumber) {
        toast.error("Please complete both airline and frequent flyer number.");
        return;
      }
      frequentFlyerMemberships.push({
        airlineName: airline.name,
        airlineCode: airline.code,
        membershipNumber: membership.membershipNumber.trim(),
      });
    }
    setSavingTraveller(true);
    try {
      const id = travellerForm.id || travellerForm._id;
      const saved = await post(id ? `/passengers/update/${id}` : "/passengers/create", {
        ...travellerForm,
        dob: travellerDob,
        frequentFlyerMemberships,
      });
      setTravellers((current) => id ? current.map((item) => (item.id || item._id) === id ? saved : item) : [saved, ...current]);
      setTravellerModal(false); toast.success(id ? "Traveller updated" : "Traveller saved");
    } catch (error) { toast.error(error?.message || "Unable to save traveller"); }
    finally { setSavingTraveller(false); }
  };

  const confirmDeleteTraveller = async () => {
    const id = deleteTraveller?.id || deleteTraveller?._id;
    if (!id) return;
    try {
      await post(`/passengers/delete/${id}`);
      setTravellers((current) => current.filter((item) => (item.id || item._id) !== id));
      setDeleteTraveller(null); toast.success("Traveller removed");
    } catch (error) { toast.error(error?.message || "Unable to remove traveller"); }
  };

  const addFrequentFlyer = () => setTravellerForm((current) => ({
    ...current, frequentFlyerMemberships: [...current.frequentFlyerMemberships, emptyFrequentFlyerMembership()],
  }));

  const updateFrequentFlyerAirline = (index, airlineCode) => setTravellerForm((current) => ({
    ...current,
    frequentFlyerMemberships: current.frequentFlyerMemberships.map((membership, membershipIndex) => {
      if (membershipIndex !== index) return membership;
      const airline = frequentFlyerAirlineByCode.get(airlineCode);
      return { ...membership, airlineName: airline?.name || "", airlineCode: airline?.code || "" };
    }),
  }));

  const updateFrequentFlyerNumber = (index, membershipNumber) => setTravellerForm((current) => ({
    ...current,
    frequentFlyerMemberships: current.frequentFlyerMemberships.map((membership, membershipIndex) => membershipIndex === index ? { ...membership, membershipNumber } : membership),
  }));

  const startContactVerification = async (channel) => {
    const destination = channel === "email" ? profileFormData.email : phone;
    if (!destination) return toast.error(`Add a ${channel} before verification`);
    setVerificationBusy(true);
    try {
      const payload = channel === "email" ? { email: destination } : { phoneAreaCode: countryCode, phoneNumber: destination };
      const result = await post(`/auth/sendProfile${channel === "email" ? "Email" : "Phone"}OTP`, payload);
      setVerificationModal({ channel, destination, verificationId: result.verificationId }); setVerificationOtp("");
      setVerificationRetryAfter(Number(result?.retryAfterSeconds || 30));
      toast.success(`Verification code sent to your ${channel}`);
    } catch (error) { toast.error(error?.message || `Unable to send ${channel} verification code`); }
    finally { setVerificationBusy(false); }
  };

  const openContactEditor = (channel) => {
    setActiveTab("profile");
    setProfileEditable(true);
    window.setTimeout(() => {
      const ref = channel === "email" ? emailInputRef : phoneInputRef;
      ref.current?.focus?.();
    }, 0);
  };

  const verifyContact = async () => {
    if (!verificationOtp.trim()) return toast.error("Enter the verification code");
    const { channel, destination, verificationId } = verificationModal;
    const payload = channel === "email"
      ? { verificationId, email: destination, otp: verificationOtp.trim() }
      : { verificationId, phoneAreaCode: countryCode, phoneNumber: destination, otp: verificationOtp.trim() };
    setVerificationBusy(true);
    try {
      const result = await post(`/auth/verify${channel === "email" ? "Email" : "Phone"}OTP`, payload);
      if (!result?.isVerified) throw new Error("The verification code is invalid or expired");
      setVerifiedContacts((current) => ({ ...current, [channel]: true }));
      setContactVerificationIds((current) => ({ ...current, [channel]: verificationId }));
      setVerificationModal(null);
      toast.success(`${channel === "email" ? "Email" : "Phone"} verified`);
    } catch (error) { toast.error(error?.message || "Verification failed"); }
    finally { setVerificationBusy(false); }
  };

  const deactivateAccount = async () => {
    setDeactivating(true);
    try {
      await post("/auth/lockAccount", { reason: deactivationReason.trim() || "user_requested_deactivation" });
      toast.success("Your account has been deactivated"); logout();
    } catch (error) { toast.error(error?.message || "Unable to deactivate account"); }
    finally { setDeactivating(false); }
  };

  const sendPasswordOtp = async () => {
    try { await sendEmailOTP(accountUser.email); setOtpSent(true); toast.success("OTP sent to your email"); }
    catch (error) { toast.error(error?.message || "Unable to send OTP"); }
  };

  if (isSessionResolving || !accountUser || !profileFormData) {
    return <><Navbar /><div className="layout-content"><section className="container p-0"><div className="mp-loading"><Skeleton active /></div></section></div><Footer /></>;
  }

  const tier = membership?.vipTier || userData?.vipTier || accountUser.vipTier || "Explorer";
  const creditBalance = Number(membership?.tellyCreditAmount ?? accountUser.telyCredits ?? 0);
  const rewardItems = [
    ...(activity.creditLogs || []).map((item) => ({ ...item, category: "Telly Credit", detail: item.notes || item.source || item.type, value: `${Number(item.amount || 0) > 0 ? "+" : ""}${Number(item.amount || 0).toFixed(2)} USD` })),
    ...(activity.xpLogs || []).map((item) => ({ ...item, category: "XP", detail: item.notes || item.actionType, value: `${Number(item.xpAmount || 0) > 0 ? "+" : ""}${Number(item.xpAmount || 0)} XP` })),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);

  return (
    <>
      <Navbar />
      <div className="layout-content mp-page-shell"><section className="container p-0"><div className="my-profile-page">
        <header className="mp-header">
          <div className="mp-avatar-wrap"><div className="mp-avatar">{accountUser.profileImageUrl ? <img src={accountUser.profileImageUrl} alt={`${accountUser.fullName || "User"} profile`} /> : <span>{(accountUser.fullName || accountUser.email || "T").charAt(0).toUpperCase()}</span>}</div><button className="mp-camera" type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto} aria-label="Upload profile photo"><Camera size={18} /></button><input ref={fileInputRef} type="file" accept="image/*" hidden onChange={uploadProfilePhoto} /></div>
          <div className="mp-header-copy"><Title level={2} className="mp-h1">{accountUser.fullName || "My Account"}</Title><Text className="mp-subtitle">Manage your profile, saved travellers, rewards, community, and account security.</Text></div>
          <div className="mp-header-badges"><span><Star size={18} /> {tier} Tier</span><span><WalletCards size={18} /> {formatCreditBalance(creditBalance)} USD Telly Credits</span></div>
        </header>

        <div className="mp-stat-grid">
          <div className="mp-stat"><Star /><span><small>Telly Tier</small><strong>{tier}</strong></span></div>
          <div className="mp-stat"><WalletCards /><span><small>Telly Credits Balance</small><strong>USD {formatCreditBalance(creditBalance)}</strong></span></div>
          <div className="mp-stat"><Users /><span><small>Followers</small><strong>{Number(moments.followerCount || 0).toLocaleString()}</strong></span></div>
          <div className="mp-stat"><UserRound /><span><small>Following</small><strong>{Number(moments.followingCount || 0).toLocaleString()}</strong></span></div>
          <div className="mp-stat"><ShieldCheck /><span><small>Identity & KYC</small><strong>{accountUser.kycVerified ? "Verified" : "Not verified"}</strong></span></div>
        </div>

        <div className="mp-layout">
          <Card className="mp-card mp-sidebar"><nav className="mp-nav">
            <button className={`mp-tab ${activeTab === "profile" ? "mp-tab-active" : ""}`} onClick={() => setActiveTab("profile")}><UserRound /><span><strong>Profile</strong><small>Account dashboard</small></span></button>
            <button className={`mp-tab ${activeTab === "bookings" ? "mp-tab-active" : ""}`} onClick={() => setActiveTab("bookings")}><CalendarDays /><span><strong>My Bookings</strong><small>Orders and trips</small></span></button>
            <button className={`mp-tab ${activeTab === "travellers" ? "mp-tab-active" : ""}`} onClick={() => setActiveTab("travellers")}><Plane /><span><strong>Traveller Information</strong><small>Travellers and memberships</small></span></button>
            <button className={`mp-tab ${activeTab === "verification" ? "mp-tab-active" : ""}`} onClick={() => setActiveTab("verification")}><ShieldCheck /><span><strong>Verification</strong><small>Contact and identity</small></span></button>
          </nav></Card>

          <main className="mp-content">{activeTab === "bookings" ? <MyBookings embedded /> : <>
            {activeTab === "profile" && <>
            <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner">
              <SectionHeader icon={UserRound} title="Profile Information" description="Personal details and preferred travel settings" action={profileEditable ? <Space><Button onClick={() => setProfileEditable(false)}>Cancel</Button><Button type="primary" onClick={saveProfileInformation}>Save changes</Button></Space> : <Button type="primary" onClick={() => setProfileEditable(true)}><Pencil size={15} /> Edit</Button>} /><Divider />
              <div className="mp-form-grid">
                <label><span>Full name</span><Input value={profileFormData.fullName} disabled={!profileEditable} onChange={(event) => setProfileFormData((current) => ({ ...current, fullName: event.target.value }))} /></label>
                <label><span>Email</span><div className="mp-contact-row"><Input ref={emailInputRef} type="email" value={profileFormData.email} disabled={!profileEditable} onChange={(event) => { const value = event.target.value; setProfileFormData((current) => ({ ...current, email: value })); setVerifiedContacts((current) => ({ ...current, email: value.trim().toLowerCase() === String(accountUser.email || "").trim().toLowerCase() && accountUser.isVerified === true })); setContactVerificationIds((current) => ({ ...current, email: null })); }} />{profileEditable && profileFormData.email.trim().toLowerCase() !== String(accountUser.email || "").trim().toLowerCase() && <Button loading={verificationBusy} onClick={() => startContactVerification("email")}>Verify new email</Button>}</div></label>
                <label><span>Date of birth (DD/MMM/YYYY)</span><Input value={profileFormData.DOB || ""} placeholder="17/Nov/1993" disabled={!profileEditable || Boolean(accountUser.DOB)} onChange={(event) => setProfileFormData((current) => ({ ...current, DOB: event.target.value }))} /></label>
                <label><span>Gender</span><Select value={profileFormData.gender || undefined} disabled={!profileEditable} onChange={(value) => setProfileFormData((current) => ({ ...current, gender: value }))} options={["Male", "Female", "Other"].map((value) => ({ value, label: value }))} /></label>
                <label><span>Preferred currency</span><Select value={profileFormData.currency} disabled={!profileEditable} onChange={(value) => setProfileFormData((current) => ({ ...current, currency: value }))} options={currencyOptions} /></label>
                <label><span>Country</span><Select showSearch filterOption={filterCountryOption} value={profileFormData.country || undefined} disabled={!profileEditable} onChange={(value) => setProfileFormData((current) => ({ ...current, country: value }))} options={countryOptions} /></label>
                <label><span>Phone</span><div className="mp-phone-row"><Select showSearch filterOption={filterCountryOption} value={countryCode} disabled={!profileEditable} onChange={(value) => { setCountryCode(value); setVerifiedContacts((current) => ({ ...current, phone: value === accountUser.phoneAreaCode && phone === accountUser.phone })); setContactVerificationIds((current) => ({ ...current, phone: null })); }} options={phoneCountryOptions} /><Input ref={phoneInputRef} value={phone} disabled={!profileEditable} onChange={(event) => { const value = event.target.value.replace(/[^0-9]/g, ""); setPhone(value); setVerifiedContacts((current) => ({ ...current, phone: value === accountUser.phone && countryCode === accountUser.phoneAreaCode })); setContactVerificationIds((current) => ({ ...current, phone: null })); }} />{profileEditable && (phone !== String(accountUser.phone || "") || countryCode !== String(accountUser.phoneAreaCode || "+65")) && <Button className="mp-verify-contact" loading={verificationBusy} onClick={() => startContactVerification("phone")}>Verify phone</Button>}</div>{phoneError && <small className="mp-error">{phoneError}</small>}</label>
              </div>
            </div></Card>

            <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={MapPin} title="Address" description="Your saved contact address" /><Divider /><div className="mp-form-grid">
              {[['line1', 'Address line 1'], ['line2', 'Address line 2'], ['city', 'City'], ['state', 'State / Province'], ['postalCode', 'Postal code']].map(([field, label], index) => <label key={field} className={index < 2 ? "mp-span-2" : ""}><span>{label}{['line1', 'city', 'postalCode'].includes(field) && ' *'}</span><Input value={addressFields[field]} disabled={!profileEditable} onChange={(event) => setAddressFields((current) => ({ ...current, [field]: event.target.value }))} /></label>)}
              <label><span>Country</span><Input value={profileFormData.country || ""} disabled /></label>
            </div></div></Card>
            </>}

            {activeTab === "verification" &&
            <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={ShieldCheck} title="Verification Status" description="Keep your contact details and identity protected" /><Divider /><div className="mp-verification-grid">
              <div><span className="mp-icon"><MailCheck /></span><div><strong>Email</strong><small>{accountUser.email}</small></div><StatusPill verified={verifiedContacts.email}>{verifiedContacts.email ? "Verified" : "Not verified"}</StatusPill><Button onClick={() => openContactEditor("email")}><Pencil size={14} /> Change</Button></div>
              <div><span className="mp-icon"><PhoneCall /></span><div><strong>Phone</strong><small>{countryCode} {phone || "Not added"}</small></div><StatusPill verified={verifiedContacts.phone}>{verifiedContacts.phone ? "Verified" : "Not verified"}</StatusPill><Button onClick={() => openContactEditor("phone")}><Pencil size={14} /> Change</Button></div>
              <div><span className="mp-icon"><ShieldCheck /></span><div><strong>Identity & KYC</strong><small>Account identity verification</small></div><StatusPill verified={accountUser.kycVerified}>{accountUser.kycVerified ? "Verified" : "Not verified"}</StatusPill></div>
            </div></div></Card>}

            {activeTab === "travellers" &&
            <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={Plane} title="Traveller Information" description="Reusable traveller and frequent flyer details" action={<Button type="primary" onClick={() => openTravellerEditor()}><Plus size={15} /> Add traveller</Button>} /><Divider />
              {loadingDashboard ? <Skeleton active /> : travellers.length === 0 ? <div className="mp-empty"><Plane /><strong>No saved travellers yet</strong><span>Add a traveller to speed up future bookings.</span></div> : <div className="mp-traveller-list">{travellers.map((traveller) => <article key={traveller.id || traveller._id} className="mp-traveller"><div className="mp-traveller-avatar">{traveller.firstName?.charAt(0) || "T"}</div><div className="mp-traveller-main"><strong>{traveller.firstName} {traveller.lastName}</strong><span>{traveller.type || "Traveller"} · Born {formatDate(traveller.dob)}</span><small>{traveller.documentType || "Passport"}: {traveller.documentNumber || "Not provided"}</small>{traveller.frequentFlyerMemberships?.length > 0 && <div className="mp-flyer-tags">{traveller.frequentFlyerMemberships.map((membership, index) => <span key={`${membership.airlineCode}-${index}`}><Plane size={13} /> {membership.airlineCode || membership.airlineName} · {membership.membershipNumber}</span>)}</div>}</div><div className="mp-inline-actions"><Button aria-label={`Edit ${traveller.firstName}`} onClick={() => openTravellerEditor(traveller)}><Pencil size={16} /></Button><Button danger aria-label={`Delete ${traveller.firstName}`} onClick={() => setDeleteTraveller(traveller)}><Trash2 size={16} /></Button></div></article>)}</div>}
            </div></Card>}

            {activeTab === "profile" && <>
            <div className="mp-two-column">
              <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={Gift} title="Referral History" description={`Your code: ${accountUser.referralCode || "Not available"}`} /><Divider />{referrals.length === 0 ? <div className="mp-empty mp-empty-compact"><Gift /><span>No referral history yet.</span></div> : <div className="mp-history">{referrals.map((item, index) => <div key={`${item.referredName}-${index}`}><span><strong>{item.referredName}</strong><small>{item.status} · {item.createdAt}</small></span><b>+{Number(item.creditsAwarded || 0).toFixed(2)} {item.currency || "USD"}</b></div>)}</div>}</div></Card>
              <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={WalletCards} title="Reward History" description={`${membership?.xp || 0} XP earned`} /><Divider />{rewardItems.length === 0 ? <div className="mp-empty mp-empty-compact"><WalletCards /><span>No reward activity yet.</span></div> : <div className="mp-history">{rewardItems.map((item, index) => <div key={`${item.id || index}-${item.category}`}><span><strong>{item.category}</strong><small>{item.detail || "Account activity"} · {formatDate(item.createdAt)}</small></span><b>{item.value}</b></div>)}</div>}</div></Card>
            </div>

            <Card className="mp-card" bodyStyle={{ padding: 0 }}><div className="mp-card-inner"><SectionHeader icon={LockKeyhole} title="Account Security" description="Password, session, and account controls" /><Divider /><div className="mp-security-actions"><Button onClick={() => setOpenChangePwd(true)}><KeyRound size={16} /> Change password</Button><Button danger onClick={() => setDeactivateModal(true)}><LockKeyhole size={16} /> Deactivate account</Button><Button className="mp-logout" onClick={logout}><LogOut size={16} /> Log out</Button></div></div></Card>
            </>}
          </>}</main>
        </div>
      </div></section></div>
      <Footer />

      <Modal title={travellerForm.id || travellerForm._id ? "Edit Traveller" : "Add Traveller"} open={travellerModal} onCancel={() => setTravellerModal(false)} width={780} footer={<><Button onClick={() => setTravellerModal(false)}>Cancel</Button><Button type="primary" loading={savingTraveller} onClick={saveTraveller}>Save traveller</Button></>}>
        <div className="mp-modal-grid">{[['firstName', 'First name', 'text'], ['lastName', 'Last name', 'text'], ['dob', 'Date of birth (DD/MM/YYYY)', 'text'], ['nationality', 'Nationality', 'text'], ['issuingCountry', 'Issuing country', 'text'], ['documentNumber', 'Document number', 'text']].map(([field, label, type]) => <label key={field}><span>{label}</span><Input type={type} placeholder={field === "dob" ? "17/11/1993" : undefined} value={travellerForm[field] || ""} onChange={(event) => setTravellerForm((current) => ({ ...current, [field]: event.target.value }))} /></label>)}<label><span>Gender</span><Select value={travellerForm.gender || undefined} onChange={(value) => setTravellerForm((current) => ({ ...current, gender: value }))} options={["Male", "Female", "Other"].map((value) => ({ value, label: value }))} /></label><label><span>Document type</span><Select value={travellerForm.documentType} onChange={(value) => setTravellerForm((current) => ({ ...current, documentType: value }))} options={["Passport", "National ID"].map((value) => ({ value, label: value }))} /></label><label><span>Document expiry</span><Input type="date" value={travellerForm.expirationDate || ""} disabled={travellerForm.noExpirationDate} onChange={(event) => setTravellerForm((current) => ({ ...current, expirationDate: event.target.value }))} /></label><label className="mp-checkbox"><input type="checkbox" checked={travellerForm.noExpirationDate} onChange={(event) => setTravellerForm((current) => ({ ...current, noExpirationDate: event.target.checked }))} /> No expiration date</label></div>
        <div className="mp-flyer-editor"><div className="mp-flyer-heading"><div><strong>Frequent Flyer Information</strong><span>Add memberships used during flight booking.</span></div><Button onClick={addFrequentFlyer}><Plus size={15} /> Add frequent flyer membership</Button></div>{travellerForm.frequentFlyerMemberships.map((membership, index) => <div className="mp-flyer-membership" key={index}><strong>Membership {index + 1}</strong><div className="mp-flyer-row"><Select showSearch optionFilterProp="label" placeholder="Frequent Flyer Airline" value={membership.airlineCode || undefined} onChange={(value) => updateFrequentFlyerAirline(index, value)} options={frequentFlyerAirlines.map((airline) => ({ value: airline.code, label: airline.label }))} /><Input placeholder="Frequent Flyer No" value={membership.membershipNumber} onChange={(event) => updateFrequentFlyerNumber(index, event.target.value)} /><Button danger disabled={travellerForm.frequentFlyerMemberships.length <= 1} aria-label={`Remove membership ${index + 1}`} onClick={() => setTravellerForm((current) => current.frequentFlyerMemberships.length <= 1 ? current : ({ ...current, frequentFlyerMemberships: current.frequentFlyerMemberships.filter((_, membershipIndex) => membershipIndex !== index) }))}><Trash2 size={15} /></Button></div></div>)}</div>
      </Modal>

      <Modal title="Remove saved traveller?" open={Boolean(deleteTraveller)} onCancel={() => setDeleteTraveller(null)} footer={<><Button onClick={() => setDeleteTraveller(null)}>Cancel</Button><Button danger type="primary" onClick={confirmDeleteTraveller}>Remove traveller</Button></>}><p>This removes {deleteTraveller?.firstName} {deleteTraveller?.lastName} and their frequent flyer information from your saved travellers.</p></Modal>
      <Modal className="mp-contact-verification-modal" title={null} open={Boolean(verificationModal)} onCancel={() => !verificationBusy && setVerificationModal(null)} footer={null} width={480} destroyOnClose>
        <div className="mp-contact-verification">
          <div className="mp-contact-verification__icon">{verificationModal?.channel === "phone" ? <PhoneCall /> : <MailCheck />}</div>
          <p className="teleport-eyebrow">Secure verification</p>
          <h2>Verify your {verificationModal?.channel}</h2>
          <p>We sent a 6-digit code to <strong>{verificationModal?.channel === "phone" ? `${countryCode} ${verificationModal?.destination}` : verificationModal?.destination}</strong>.</p>
          <label><span>Verification code</span><Input autoFocus inputMode="numeric" autoComplete="one-time-code" value={verificationOtp} onChange={(event) => setVerificationOtp(event.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="000000" onPressEnter={() => verificationOtp.length === 6 && verifyContact()} /></label>
          <button type="button" className="mp-resend-code" disabled={verificationBusy || verificationRetryAfter > 0} onClick={() => startContactVerification(verificationModal.channel)}>{verificationRetryAfter > 0 ? `Resend code in ${verificationRetryAfter}s` : "Resend verification code"}</button>
          <div className="mp-contact-verification__actions"><Button onClick={() => setVerificationModal(null)} disabled={verificationBusy}>Cancel</Button><Button type="primary" loading={verificationBusy} disabled={verificationOtp.length !== 6} onClick={verifyContact}>Verify code</Button></div>
          <small>Codes expire after 30 minutes. Never share this code with anyone.</small>
        </div>
      </Modal>
      <Modal title="Deactivate account?" open={deactivateModal} onCancel={() => setDeactivateModal(false)} footer={<><Button onClick={() => setDeactivateModal(false)}>Keep account</Button><Button danger type="primary" loading={deactivating} onClick={deactivateAccount}>Deactivate account</Button></>}><div className="mp-danger-copy"><AlertTriangle /><div><strong>This will lock your account immediately.</strong><p>You will be signed out and will need support assistance to restore access. Your existing booking records are retained.</p></div></div><label className="mp-modal-label"><span>Reason (optional)</span><Input.TextArea rows={3} value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} placeholder="Tell us why you are leaving" /></label></Modal>
      <Modal className="mp-password-modal" width={620} title={null} open={openChangePwd} onCancel={() => { setOpenChangePwd(false); formik.resetForm(); }} footer={null}>
        <div className="mp-password-header"><span><KeyRound size={22} /></span><div><h2>Change password</h2><p>Use a strong password you do not reuse elsewhere.</p></div></div>
        <Form className="mp-password-form" layout="vertical">
          <Form.Item className="mp-password-current" label="Current password" validateStatus={formik.touched.currentPassword && formik.errors.currentPassword ? "error" : undefined} help={formik.touched.currentPassword && formik.errors.currentPassword}><Input.Password name="currentPassword" autoComplete="current-password" value={formik.values.currentPassword} onChange={formik.handleChange} onBlur={formik.handleBlur} /></Form.Item>
          <div className="mp-password-grid">{[['newPassword', 'New password'], ['confirmNewPassword', 'Confirm new password']].map(([name, label]) => <Form.Item key={name} label={label} validateStatus={formik.touched[name] && formik.errors[name] ? "error" : undefined} help={formik.touched[name] && formik.errors[name]}><Input.Password name={name} autoComplete="new-password" value={formik.values[name]} onChange={formik.handleChange} onBlur={formik.handleBlur} /></Form.Item>)}</div>
          <div className="mp-password-otp"><Form.Item label="Email verification code" validateStatus={formik.touched.otp && formik.errors.otp ? "error" : undefined} help={formik.touched.otp && formik.errors.otp}><Input name="otp" inputMode="numeric" maxLength={8} value={formik.values.otp} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Enter the code sent to your email" /></Form.Item><Button onClick={sendPasswordOtp}>{otpSent ? "Resend code" : "Send code"}</Button></div>
          <div className="mp-password-note"><ShieldCheck size={17} /><span>For your security, the verification code is sent to <strong>{accountUser.email}</strong>.</span></div>
          <div className="mp-password-actions"><Button onClick={() => { setOpenChangePwd(false); formik.resetForm(); }}>Cancel</Button><Button type="primary" loading={formik.isSubmitting} onClick={formik.submitForm}>Update password</Button></div>
        </Form>
      </Modal>
    </>
  );
};

export default MyProfile;
